import { useGetTripLog } from '@agos/api-client/orders'
import { formatCents, formatDayMonth, formatDecimal, formatTime, OrderNumberLink, T, toDateParam, tt, useTheme } from '@agos/shared'
import { EmptyState, PageHeader, StatCard, cn, BUTTON_PRIMARY} from '@agos/ui'
import { Book, Car, MapPin, Printer } from 'lucide-react'
import { useMemo, useState } from 'react'

import {
  addressLine,
  filterRuns,
  groupByVehicle,
  isRunOpen,
  runCostCents,
  runCountLabel,
  tripTotals,
  vehicleOptions,
  type TripRun,
} from './trip-log'

/**
 * `/trip-log` — the *kniha jázd*, ported from legacy `pages/trip-log.tsx`.
 *
 * The record a Slovak business keeps to justify fuel as a business expense. It is
 * printed and filed, so the page carries its own print stylesheet and its totals
 * have to reconcile with the fleet page's — both read the same `fuelCostCents`,
 * which is what makes that guaranteed rather than hoped for.
 */

/** A fortnight back, which is what a monthly filing usually wants. */
const DEFAULT_DAYS = 14

const PRINT_STYLES = `
@media print {
  @page { size: A4 landscape; margin: 12mm; }
  body * { visibility: hidden; }
  #trip-log-print, #trip-log-print * { visibility: visible; }
  #trip-log-print { position: absolute; inset: 0; width: 100%; }
  .trip-log-screen-only { display: none !important; }
}
`

function daysAgo(days: number): string {
  const date = new Date()
  date.setDate(date.getDate() - days)
  return toDateParam(date)
}

export function TripLogPage() {
  const [dateFrom, setDateFrom] = useState(() => daysAgo(DEFAULT_DAYS))
  const [dateTo, setDateTo] = useState(() => toDateParam(new Date()))
  const [vehicle, setVehicle] = useState('')
  const [search, setSearch] = useState('')

  const { restaurantName } = useTheme()
  /** §9.317 - fixed at mount, so the sheet does not claim a time a re-render happened to have. */
  const [printedOn] = useState(() =>
    new Date().toLocaleString('sk-SK', {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }),
  )

  const { data, isLoading } = useGetTripLog({ from: dateFrom, to: dateTo })
  const runs = useMemo(() => (Array.isArray(data) ? (data as TripRun[]) : []), [data])

  const options = useMemo(() => vehicleOptions(runs), [runs])
  const filtered = useMemo(() => filterRuns(runs, { vehicle, search }), [runs, vehicle, search])
  const groups = useMemo(() => groupByVehicle(filtered), [filtered])
  const totals = useMemo(() => tripTotals(filtered), [filtered])

  return (
    <div className="space-y-4">
      <style>{PRINT_STYLES}</style>

      <PageHeader
        title={tt('Kniha jázd')}
        description={tt('Záznam jázd pre daňové účely.')}
        icon={<Book className="w-5 h-5" />}
        actions={
          <button
            type="button"
            onClick={() => window.print()}
            className={cn('flex items-center gap-1.5 px-3 py-2 rounded-lg', BUTTON_PRIMARY, 'text-xs')}
          >
            <Printer className="w-3.5 h-3.5" /> <T>Tlačiť</T>
          </button>
        }
      />

      <div className="trip-log-screen-only flex flex-wrap gap-2 items-end">
        <label className="space-y-1">
          <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider block">
            <T>Od</T>
          </span>
          <input
            type="date"
            value={dateFrom}
            onChange={(event) => setDateFrom(event.target.value)}
            aria-label={tt('Od')}
            className="px-3 py-2 rounded-xl bg-card border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20"
          />
        </label>
        <label className="space-y-1">
          <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider block">
            <T>Do</T>
          </span>
          <input
            type="date"
            value={dateTo}
            onChange={(event) => setDateTo(event.target.value)}
            aria-label={tt('Do')}
            className="px-3 py-2 rounded-xl bg-card border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20"
          />
        </label>
        <label className="space-y-1">
          <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider block">
            <T>Vozidlo</T>
          </span>
          <select
            value={vehicle}
            onChange={(event) => setVehicle(event.target.value)}
            aria-label={tt('Vozidlo')}
            className="px-3 py-2 rounded-xl bg-card border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20"
          >
            <option value="">{tt('Všetky vozidlá')}</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className="space-y-1 flex-1 min-w-[180px]">
          <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider block">
            <T>Hľadať</T>
          </span>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={tt('Šofér, značka, zákazník, adresa...')}
            aria-label={tt('Hľadať')}
            className="w-full px-3 py-2 rounded-xl bg-card border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20"
          />
        </label>
      </div>

      <div className="trip-log-screen-only grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label={tt('Jázd')} value={String(totals.runCount)} icon={Car} color="violet" />
        <StatCard
          label={tt('Celkové km')}
          value={`${formatDecimal(totals.totalKm, 1)} km`}
          icon={MapPin}
          color="blue"
        />
        <StatCard
          label={tt('Palivo')}
          value={`${formatDecimal(totals.totalLitres, 1)} l`}
          icon={Car}
          color="amber"
        />
        <StatCard
          label={tt('Náklady')}
          value={formatCents(totals.totalCostCents)}
          icon={Car}
          color="emerald"
        />
      </div>

      <div id="trip-log-print" className="bg-card border border-border rounded-2xl p-5 space-y-5">
        {/* §9.317 - the franchise's own name on the sheet, as legacy's print header has it. A filed
          * copy that does not say whose it is has to be identified from the plates. */}
        <div className="hidden print:block space-y-1" data-print-header>
          <h1 className="text-lg font-bold">
            {restaurantName} <T>- Kniha jázd</T>
          </h1>
          <p className="text-sm text-muted-foreground">
            {formatDayMonth(dateFrom)} - {formatDayMonth(dateTo)} · {totals.runCount}{' '}
            {tt('jázd')} · {formatDecimal(totals.totalKm, 1)} km ·{' '}
            {formatCents(totals.totalCostCents)}
          </p>
        </div>

        {isLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((placeholder) => (
              <div
                key={placeholder}
                className="h-24 rounded-xl bg-muted/40 border border-border animate-pulse"
              />
            ))}
          </div>
        ) : groups.length === 0 ? (
          <EmptyState
            icon={<Book className="w-10 h-10" />}
            title={tt('Žiadne jazdy')}
            description={tt('V tomto období nie sú zaznamenané žiadne jazdy.')}
          />
        ) : (
          groups.map((group) => (
            <section key={group.vehicleLicensePlate || group.vehicleName} className="space-y-2">
              <div className="flex flex-wrap items-baseline gap-2 border-b border-border pb-1.5">
                <h2 className="font-bold">{group.vehicleName}</h2>
                <span className="font-mono text-sm text-muted-foreground">
                  {group.vehicleLicensePlate}
                </span>
                <span className="text-xs text-muted-foreground">
                  {group.drivers.join(', ')}
                </span>
                <span className="ml-auto font-bold tabular-nums">
                  {formatDecimal(group.totalKm, 1)} km
                </span>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                      <th className="text-left py-1.5 pr-3">
                        <T>Dátum</T>
                      </th>
                      <th className="text-left py-1.5 pr-3">
                        <T>Odchod</T>
                      </th>
                      <th className="text-left py-1.5 pr-3">
                        <T>Príchod</T>
                      </th>
                      <th className="text-left py-1.5 pr-3">
                        <T>Šofér</T>
                      </th>
                      {/* §9.317, owner Q3 - the column follows the medium. A link into the archive is
                        * worthless on paper; an address is the only thing that makes a printed run
                        * auditable. */}
                      <th className="text-left py-1.5 pr-3 trip-log-screen-only">
                        <T>Zastávky</T>
                      </th>
                      <th className="text-left py-1.5 pr-3 hidden print:table-cell">
                        <T>Adresy doručenia</T>
                      </th>
                      <th className="text-right py-1.5 pr-3">km</th>
                      <th className="text-right py-1.5">
                        <T>Náklady</T>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.runs.map((run) => (
                      <tr key={run.deliveryRunId} className="border-t border-border/60">
                        <td className="py-1.5 pr-3 whitespace-nowrap">
                          {run.departedAt ? formatDayMonth(run.departedAt) : '-'}
                        </td>
                        <td className="py-1.5 pr-3 tabular-nums">
                          {run.departedAt ? formatTime(run.departedAt) : '-'}
                        </td>
                        <td
                          className={cn(
                            'py-1.5 pr-3 tabular-nums',
                            isRunOpen(run) && 'text-amber-600 dark:text-amber-400 font-semibold',
                          )}
                        >
                          {/* Still out. The kilometres already driven still count. */}
                          {run.completedAt ? formatTime(run.completedAt) : tt('na ceste')}
                        </td>
                        <td className="py-1.5 pr-3">{run.driverName}</td>
                        <td className="py-1.5 pr-3 text-xs text-muted-foreground trip-log-screen-only">
                          {/* §9.143 - a completed round; each order it carried links to the
                            * archive. Rendered as elements rather than a joined string, which is
                            * what it was, because a string cannot hold a link. */}
                          {(run.orders ?? []).length === 0
                            ? '-'
                            : (run.orders ?? []).map((order, index) => (
                                <span key={order.id ?? index}>
                                  {index > 0 && ' · '}
                                  <OrderNumberLink
                                    orderId={order.id}
                                    className="text-primary hover:underline"
                                    title={`${tt('Otvoriť v archíve')}: #${order.orderNumber ?? ''}`}
                                  >
                                    #{order.orderNumber}
                                  </OrderNumberLink>
                                  {order.customerName !== undefined && ` ${order.customerName}`}
                                </span>
                              ))}
                        </td>
                        {/* §9.317 - where the run actually went, which is what the filed sheet is
                          * for. Joined with an arrow, as legacy's print block does. */}
                        <td
                          className="py-1.5 pr-3 text-xs leading-relaxed hidden print:table-cell"
                          data-run-addresses={run.deliveryRunId}
                        >
                          {addressLine(run)}
                        </td>
                        <td className="py-1.5 pr-3 text-right tabular-nums">
                          {formatDecimal(run.totalDistanceKm ?? 0, 1)}
                        </td>
                        <td className="py-1.5 text-right tabular-nums">
                          {formatCents(runCostCents(run))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {/* §9.317 - legacy closes every vehicle with its own total, and a printed page is
                    * where that matters: the header scrolls off, the footer sits under the rows it
                    * adds up. */}
                  <tfoot>
                    <tr className="border-t-2 border-border font-bold" data-vehicle-total>
                      <td className="py-1.5 pr-3" colSpan={5}>
                        <T>Spolu</T> ({runCountLabel(group.runs.length)})
                      </td>
                      <td className="py-1.5 pr-3 text-right tabular-nums">
                        {formatDecimal(group.totalKm, 1)}
                      </td>
                      <td className="py-1.5" />
                    </tr>
                  </tfoot>
                </table>
              </div>
            </section>
          ))
        )}

        {groups.length > 0 && (
          <div className="flex flex-wrap items-baseline gap-2 border-t-2 border-border pt-2 font-bold">
            <T>Celkovo</T>
            {/* Named, because a spec reading *"the last span under the print region"* picked up the
              * §9.317 print footer's restaurant name the moment that footer existed. */}
            <span className="ml-auto tabular-nums" data-grand-total>
              {formatDecimal(totals.totalKm, 1)} km · {formatDecimal(totals.totalLitres, 1)} l ·{' '}
              {formatCents(totals.totalCostCents)}
            </span>
          </div>
        )}

        {/* §9.317 - when this copy was printed, which is what makes a filed sheet identifiable a
          * year later. Legacy's print footer, and print-only for the same reason it is there. */}
        <div
          className="hidden print:flex flex-wrap items-baseline gap-2 text-[11px] text-muted-foreground pt-1"
          data-print-footer
        >
          <span>
            <T>Vytlačené</T>: {printedOn}
          </span>
          <span className="ml-auto">{restaurantName}</span>
        </div>
      </div>
    </div>
  )
}
